import './MaterialBalance.css';
import { GameState, PieceType, Color } from '../../engine/types';

/**
 * Props for MaterialBalance component
 */
interface MaterialBalanceProps {
  gameState: GameState;
}

/**
 * Piece values for material calculation (standard chess piece values)
 */
const PIECE_VALUES: Record<PieceType, number> = {
  [PieceType.Pawn]: 1,
  [PieceType.Knight]: 3,
  [PieceType.Bishop]: 3,
  [PieceType.Rook]: 5,
  [PieceType.Queen]: 9,
  [PieceType.King]: 0, // King has no material value
};

/**
 * Calculate total material value for a given color
 */
function calculateMaterialValue(gameState: GameState, color: Color): number {
  let total = 0;
  for (let i = 0; i < 64; i++) {
    const piece = gameState.board[i];
    if (piece && piece.color === color) {
      total += PIECE_VALUES[piece.type];
    }
  }
  return total;
}

/**
 * Count pieces by type for a given color
 */
function countPieces(gameState: GameState, color: Color): Record<PieceType, number> {
  const counts: Record<PieceType, number> = {
    [PieceType.Pawn]: 0,
    [PieceType.Knight]: 0,
    [PieceType.Bishop]: 0,
    [PieceType.Rook]: 0,
    [PieceType.Queen]: 0,
    [PieceType.King]: 0,
  };
  
  for (let i = 0; i < 64; i++) {
    const piece = gameState.board[i];
    if (piece && piece.color === color) {
      counts[piece.type]++;
    }
  }
  
  return counts;
}

/**
 * Get Unicode symbol for piece type and color
 */
function getPieceSymbol(type: PieceType, color: Color): string {
  const symbols = {
    [Color.White]: ['♙', '♘', '♗', '♖', '♕', '♔'],
    [Color.Black]: ['♟', '♞', '♝', '♜', '♛', '♚'],
  };
  return symbols[color][type];
}

/**
 * MaterialBalance component displays material count and advantage
 */
export function MaterialBalance({ gameState }: MaterialBalanceProps) {
  const whiteMaterial = calculateMaterialValue(gameState, Color.White);
  const blackMaterial = calculateMaterialValue(gameState, Color.Black);
  const materialDifference = whiteMaterial - blackMaterial;
  
  const whiteCounts = countPieces(gameState, Color.White);
  const blackCounts = countPieces(gameState, Color.Black);

  return (
    <div className="material-balance">
      <h3>Material Balance</h3>
      
      <div className="material-section">
        <div className="material-player white-material">
          <div className="material-header">
            <span className="material-label">White</span>
            <span className="material-value">{whiteMaterial}</span>
          </div>
          <div className="material-pieces">
            {Object.entries(whiteCounts).map(([typeStr, count]) => {
              const type = parseInt(typeStr) as PieceType;
              // Skip king and pieces with 0 count
              if (type === PieceType.King || count === 0) return null;
              return (
                <span key={type} className="piece-count">
                  <span className="piece-symbol">{getPieceSymbol(type, Color.White)}</span>
                  <span className="count-number">×{count}</span>
                </span>
              );
            })}
          </div>
        </div>

        <div className="material-advantage">
          {materialDifference > 0 && (
            <div className="advantage white-advantage">
              White +{materialDifference}
            </div>
          )}
          {materialDifference < 0 && (
            <div className="advantage black-advantage">
              Black +{Math.abs(materialDifference)}
            </div>
          )}
          {materialDifference === 0 && (
            <div className="advantage equal">
              Equal
            </div>
          )}
        </div>

        <div className="material-player black-material">
          <div className="material-header">
            <span className="material-label">Black</span>
            <span className="material-value">{blackMaterial}</span>
          </div>
          <div className="material-pieces">
            {Object.entries(blackCounts).map(([typeStr, count]) => {
              const type = parseInt(typeStr) as PieceType;
              // Skip king and pieces with 0 count
              if (type === PieceType.King || count === 0) return null;
              return (
                <span key={type} className="piece-count">
                  <span className="piece-symbol">{getPieceSymbol(type, Color.Black)}</span>
                  <span className="count-number">×{count}</span>
                </span>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
